import React, { useEffect } from 'react';

import { Box, Text } from '@rbilabs/universal-components';
import { addressExists } from 'features/store/pages/delivery-store-selector/utils';

import { IServerOrder } from '@rbi-ctg/menu';
import { ISaveDeliveryAddressInput, IsoCountryCode } from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { useOrderContext } from 'state/order';

import { CardHeading, CardHeadingContainer, CardText } from '../styled';

const DeliveryInfo = ({ serverOrder }: { serverOrder: IServerOrder }) => {
  const { delivery, cart } = serverOrder;
  const { user, saveDeliveryAddress } = useAuthContext();
  const {
    deliveryAddress,
    formShouldSaveDeliveryAddress,
    orderPhoneNumber,
    setFormShouldSaveDeliveryAddress,
    setShouldSaveDeliveryAddress,
    updateShouldSaveDeliveryAddress,
  } = useOrderContext();

  const enrichedAddress = {
    ...deliveryAddress,
    phoneNumber: orderPhoneNumber,
    addressLine1: deliveryAddress?.addressLine1 ?? '',
    city: deliveryAddress?.city ?? '',
    state: deliveryAddress?.state ?? '',
    zip: deliveryAddress?.zip ?? '',
    country: deliveryAddress?.country ?? '',
  };

  const isAuthenticated = Boolean(user?.cognitoId && user?.details);
  const found = addressExists(user?.details?.deliveryAddresses || [], enrichedAddress);

  useEffect(() => {
    if (isAuthenticated && deliveryAddress && formShouldSaveDeliveryAddress && !found) {
      const saveDeliveryAddressInput: ISaveDeliveryAddressInput = {
        addressLine1: deliveryAddress.addressLine1,
        addressLine2: deliveryAddress.addressLine2,
        alias: deliveryAddress.alias,
        city: deliveryAddress.city,
        state: deliveryAddress.state,
        zip: deliveryAddress.zip,
        country: deliveryAddress.country as IsoCountryCode,
        phoneNumber: deliveryAddress.phoneNumber ?? orderPhoneNumber,
        defaultInstruction: deliveryAddress.defaultInstruction,
        route: deliveryAddress.route,
        streetNumber: deliveryAddress.streetNumber,
        latitude: deliveryAddress.latitude,
        longitude: deliveryAddress.longitude,
      };
      saveDeliveryAddress(saveDeliveryAddressInput);
      setFormShouldSaveDeliveryAddress(false);
      setShouldSaveDeliveryAddress(false);
      updateShouldSaveDeliveryAddress(false);
    }
  }, [
    isAuthenticated,
    deliveryAddress,
    formShouldSaveDeliveryAddress,
    found,
    orderPhoneNumber,
    saveDeliveryAddress,
    setFormShouldSaveDeliveryAddress,
    setShouldSaveDeliveryAddress,
    updateShouldSaveDeliveryAddress,
  ]);

  return (
    <CardHeadingContainer>
      <CardHeading>{t('deliveryInfo')}</CardHeading>

      <Box marginTop="$4">
        <CardText testID="order-confirmation-customer-name" data-private>
          {cart.customerName}
        </CardText>
        <CardText testID="order-confirmation-dropoff-address" data-private>
          {`${delivery?.dropoff.addressLine1}\n`}
          {!!delivery?.dropoff.addressLine2 && (
            <>{`${t('unitNumber', { unitNumber: delivery?.dropoff.addressLine2 })}\n`}</>
          )}
          {delivery?.dropoff.city}, {delivery?.dropoff.state} {delivery?.dropoff.zip}
        </CardText>
        {!!delivery?.dropoff.phoneNumber && (
          <CardText testID="order-confirmation-phone-number" data-private>
            {delivery?.dropoff.phoneNumber}
          </CardText>
        )}
        {!!delivery?.instructions && (
          <CardText testID="order-confirmation-instructions" data-private marginTop={4}>
            <Text>{t('deliveryInstructions')}: </Text>
            {`\n${delivery?.instructions}`}
          </CardText>
        )}
      </Box>
    </CardHeadingContainer>
  );
};

export default DeliveryInfo;
